<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Add16 } from '@carbon/icons-vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'dashboard/components-next/polaris/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'dashboard/components-next/polaris/ui/popover';

// Rebuilt inside on the kit's AddDataMenu (phase 9.8): a button that opens a searchable list (registry popover +
// command) of what is not added yet; picking one adds it and closes the list. Same props and `add` event; each
// item keeps the woot's mark (icon, colour dot or face) and its email or phone.
defineProps({
  label: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['add']);
const { t } = useI18n();
const open = ref(false);

const add = item => {
  open.value = false;
  emit('add', item);
};
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button type="button" variant="outline" size="sm" data-add-data>
        <Add16 aria-hidden="true" />
        {{ label }}
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-72 p-0" align="start">
      <p
        v-if="!items.length"
        class="px-3 py-3 type-polaris-body text-polaris-muted-foreground"
      >
        {{ t('SETTINGS_PIECES.ADD_DATA.EMPTY') }}
      </p>
      <Command v-else>
        <CommandInput :placeholder="searchPlaceholder" />
        <CommandList>
          <CommandEmpty>
            {{ t('SETTINGS_PIECES.ADD_DATA.NO_MATCH') }}
          </CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="item in items"
              :key="item.id"
              :value="`${item.name || item.title} ${item.email || item.phoneNumber || ''}`"
              @select="add(item)"
            >
              <Icon
                v-if="item.icon"
                :icon="item.icon"
                class="size-4 shrink-0"
              />
              <span
                v-else-if="item.color"
                :style="{ backgroundColor: item.color }"
                class="size-2.5 shrink-0 rounded-full"
              />
              <Avatar
                v-else
                :src="item.avatarUrl"
                :name="item.name"
                :size="24"
                rounded-full
              />
              <span class="grid min-w-0">
                <span class="truncate">{{ item.name || item.title }}</span>
                <span
                  v-if="item.email || item.phoneNumber"
                  class="truncate type-polaris-caption text-polaris-muted-foreground"
                >
                  {{ item.email || item.phoneNumber }}
                </span>
              </span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
